/**
 * TARİH YARDIMCILARI (ön yüz) — YEREL takvim günü esaslı.
 *
 * ⚠ NEDEN `toISOString().slice(0,10)` KULLANILMIYOR
 * `toISOString()` UTC'ye çevirir. Türkiye UTC+3 olduğu için gece 00:00–02:59 arasında
 * `new Date().toISOString().slice(0,10)` DÜNÜN tarihini döndürür. Vardiyalı bir üretim
 * tesisinde gece 01:00'de sipariş açan kullanıcıya bir önceki günü varsayılan yapmak
 * sessiz bir veri hatasıdır. (ConsumptionEntryDrawer.tsx:46 bunu `Date.now() + 3*3600*1000`
 * ile yamıyor; sabit ofset yerine yerel takvim alanlarını okumak doğrusudur.)
 *
 * Buradaki tüm fonksiyonlar "YYYY-MM-DD" metniyle çalışır — `<input type="date">` ve
 * `DateField` ile aynı sözleşme.
 */

const p2 = (n: number) => String(n).padStart(2, "0");

/** Kullanıcının YEREL takvim günü, "YYYY-MM-DD". */
export function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
}

/** Date → "YYYY-MM-DD" (yerel alanlardan; UTC kaydırması yok). */
export function toISO(d: Date): string {
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
}

/**
 * "YYYY-MM-DD" → YEREL gece yarısı Date.
 * `new Date("2026-08-20")` UTC gece yarısı verir; UTC+3'te yerel saat 03:00 olur ve
 * `getDate()` doğru olsa da negatif ofsetli bir makinede GÜN KAYAR. Alanları tek tek
 * vererek bu belirsizliği bitiriyoruz. Geçersiz metinde `null`.
 */
export function fromISO(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const y = Number(m[1]), mo = Number(m[2]), d = Number(m[3]);
  const dt = new Date(y, mo - 1, d);
  // Taşan girdiyi (2026-02-31 → 3 Mart) sessizce kabul etme.
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null;
  return dt;
}

/** "YYYY-MM-DD" → "20.08.2026" (CLAUDE.md ✅#4). Geçersizse boş metin. */
export function fmtTR(s: string): string {
  const d = fromISO(s);
  return d ? `${p2(d.getDate())}.${p2(d.getMonth() + 1)}.${d.getFullYear()}` : "";
}

/** Aya gün ekle/çıkar — takvim gezinmesi için (ay sonu taşmasını kırpar: 31 Oca +1 ay = 28/29 Şub). */
export function addMonths(d: Date, n: number): Date {
  const y = d.getFullYear(), m = d.getMonth(), day = d.getDate();
  const son = new Date(y, m + n + 1, 0).getDate(); // hedef ayın son günü
  return new Date(y, m + n, Math.min(day, son));
}

/** Güne gün ekle/çıkar. */
export function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

export const AY_ADLARI = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık",
] as const;

/** Pazartesi ilk gün — Türkiye takvim düzeni (JS'in Pazar-ilk düzeni DEĞİL). */
export const GUN_KISA = ["Pt", "Sa", "Ça", "Pe", "Cu", "Ct", "Pa"] as const;

/** JS `getDay()` (0=Pazar) → Pazartesi-ilk sütun indeksi (0=Pazartesi). */
export function pzIndex(d: Date): number {
  return (d.getDay() + 6) % 7;
}

/* ══════════════════ HIZLI TARİH ARALIKLARI ══════════════════ */

export type HizliAralik = "buHafta" | "buAy" | "gecenAy" | "son30";

export const HIZLI_ARALIKLAR: { key: HizliAralik; label: string }[] = [
  { key: "buHafta", label: "Bu hafta" },
  { key: "buAy", label: "Bu ay" },
  { key: "gecenAy", label: "Geçen ay" },
  { key: "son30", label: "Son 30 gün" },
];

/**
 * Hazır aralık → { from, to } (ikisi de "YYYY-MM-DD", uç değerler DAHİL).
 *
 * @param bugun Referans gün — VARSAYILAN yerel bugün. Test için dışarıdan verilebilir;
 *              böylece "bugün"e bağlı bir davranış deterministik doğrulanabilir.
 *
 * Kararlar:
 *  · Hafta PAZARTESİ başlar (TR düzeni; JS'in Pazar-ilk varsayımı DEĞİL).
 *  · "Bu hafta"/"Bu ay" İÇİNDE BULUNULAN dönemin TAMAMIDIR — gelecek günler de dahildir.
 *    Teslim tarihi ileri dönük bir alan olduğu için aralığı bugünde kesmek, bu hafta
 *    teslim edilecek siparişleri gizlerdi.
 *  · "Son 30 gün" bugün DAHİL 30 gün (bugün-29 … bugün) — 31 değil.
 */
export function hizliAralik(key: HizliAralik, bugun: Date = new Date()): { from: string; to: string } {
  const b = new Date(bugun.getFullYear(), bugun.getMonth(), bugun.getDate());
  switch (key) {
    case "buHafta": {
      const bas = addDays(b, -pzIndex(b));
      return { from: toISO(bas), to: toISO(addDays(bas, 6)) };
    }
    case "buAy": {
      const bas = new Date(b.getFullYear(), b.getMonth(), 1);
      const son = new Date(b.getFullYear(), b.getMonth() + 1, 0); // sonraki ayın 0. günü = bu ayın sonu
      return { from: toISO(bas), to: toISO(son) };
    }
    case "gecenAy": {
      const bas = new Date(b.getFullYear(), b.getMonth() - 1, 1);
      const son = new Date(b.getFullYear(), b.getMonth(), 0);
      return { from: toISO(bas), to: toISO(son) };
    }
    case "son30":
      return { from: toISO(addDays(b, -29)), to: toISO(b) };
  }
}
